<template>
  <v-dialog :value="value" max-width="820px" @input="onDialogInput">
    <v-card>
      <v-card-title>
        <span class="headline">{{ dialogTitle }}</span>
        <v-spacer />
        <v-btn icon flat @click="close">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="mapping" :value="true" type="info" outline class="mb-3">
          Editing <strong>{{ mapping.idTag }}</strong>
          <span v-if="mapping.licensePlateNumber"> for {{ mapping.licensePlateNumber }}</span>
          <span v-if="mapping.fingerprintStatus"> ({{ mapping.fingerprintStatus }})</span>
        </v-alert>

        <v-layout row wrap>
          <v-flex xs12 md6 pa-1>
            <div v-if="lockedVehicle" class="mb-3">
              <div class="caption grey--text text--darken-1">Vehicle</div>
              <div>
                <strong>{{ lockedVehicle.licensePlateNumber || lockedVehicle.id }}</strong>
                <small v-if="lockedVehicle.shortcodeId" class="ml-1">({{ lockedVehicle.shortcodeId }})</small>
              </div>
            </div>
            <transportation-profile-autocomplete
              v-else
              v-model="form.transportationProfile"
              label="Vehicle"
              placeholder="Search by plate or shortcode"
              :show-owner-account="true"
              :show-driver="true"
              :disabled="apiInProgress"
            />
          </v-flex>
          <v-flex xs12 md6 pa-1>
            <v-select
              v-model="form.vehicleClass"
              :items="vehicleClasses"
              item-text="text"
              item-value="value"
              label="Vehicle class"
              :disabled="apiInProgress"
            />
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs12 pa-1>
            <v-checkbox
              v-model="form.useProfileIdAsIdTag"
              hide-details
              :disabled="apiInProgress || !selectedTransportationProfileId"
              label="Use selected vehicle transportation profile ID as idTag"
            />
          </v-flex>
          <v-flex xs12 pa-1>
            <v-text-field
              v-model.trim="form.idTag"
              label="idTag"
              placeholder="RFID/card/app tag"
              :readonly="form.useProfileIdAsIdTag"
              :disabled="apiInProgress"
            />
          </v-flex>
          <v-flex xs12 pa-1>
            <v-textarea
              v-model="form.notes"
              rows="2"
              auto-grow
              label="Notes (optional)"
              :disabled="apiInProgress"
            />
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-btn flat color="warning" :disabled="apiInProgress || deleteInProgress" @click="close">Cancel</v-btn>
        <v-btn
          v-if="mapping"
          flat
          color="error"
          :loading="deleteInProgress"
          :disabled="apiInProgress || deleteInProgress"
          @click="deleteMapping"
        >
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="apiInProgress"
          :disabled="!canSave || deleteInProgress"
          @click="save"
        >
          {{ mapping ? 'Update idTag' : 'Add idTag' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { addAlert } from '@/utilities/helpers'
import {
  assignOcppIdTagProfile,
  deleteOcppIdTagAssignment
} from '@/services/ocpp-id-tag-service'

export default {
  name: 'ocpp-id-tag-dialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    mapping: {
      type: Object,
      default: null
    },
    lockedVehicle: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      apiInProgress: false,
      deleteInProgress: false,
      form: this.defaultForm(),
      vehicleClasses: [
        { text: 'Fleet bus', value: 'fleet_bus' },
        { text: 'Private vehicle', value: 'private_vehicle' }
      ]
    }
  },
  computed: {
    dialogTitle () {
      return this.mapping ? 'Update OCPP idTag' : 'Add OCPP idTag'
    },
    selectedTransportationProfileId () {
      const profile = this.form.transportationProfile
      return profile && profile.id ? profile.id : null
    },
    resolvedIdTag () {
      if (this.form.useProfileIdAsIdTag) {
        return this.selectedTransportationProfileId || ''
      }
      return this.form.idTag ? this.form.idTag.trim() : ''
    },
    canSave () {
      return !this.apiInProgress &&
        !!this.selectedTransportationProfileId &&
        !!this.resolvedIdTag
    }
  },
  watch: {
    value (isOpen) {
      if (isOpen) {
        this.hydrateForm()
      }
    },
    mapping () {
      if (this.value) {
        this.hydrateForm()
      }
    },
    lockedVehicle () {
      if (this.value) {
        this.hydrateForm()
      }
    },
    selectedTransportationProfileId (nextValue) {
      if (this.form.useProfileIdAsIdTag) {
        this.form.idTag = nextValue || ''
      }
    },
    'form.useProfileIdAsIdTag' (enabled) {
      if (enabled) {
        this.form.idTag = this.selectedTransportationProfileId || ''
      } else if (this.mapping && this.mapping.idTag) {
        this.form.idTag = this.mapping.idTag
      } else {
        this.form.idTag = ''
      }
    }
  },
  methods: {
    defaultForm () {
      return {
        idTag: '',
        originalIdTag: '',
        originalTransportationProfileId: '',
        transportationProfile: null,
        vehicleClass: 'fleet_bus',
        notes: '',
        useProfileIdAsIdTag: false
      }
    },
    profileFromLockedVehicle () {
      if (!this.lockedVehicle) {
        return null
      }
      return {
        id: this.lockedVehicle.id,
        licensePlateNumber: this.lockedVehicle.licensePlateNumber || this.lockedVehicle.id,
        shortcodeId: this.lockedVehicle.shortcodeId || null,
        ownerAccountName: this.lockedVehicle.ownerAccountName || null
      }
    },
    profileFromMapping () {
      if (!this.mapping || !this.mapping.transportationProfileId) {
        return null
      }
      return {
        id: this.mapping.transportationProfileId,
        licensePlateNumber: this.mapping.licensePlateNumber || this.mapping.transportationProfileId,
        shortcodeId: this.mapping.shortcodeId || null,
        ownerAccountName: this.mapping.transportationOwnerName || null
      }
    },
    hydrateForm () {
      const profile = this.profileFromLockedVehicle() || this.profileFromMapping()
      const idTag = this.mapping && this.mapping.idTag ? this.mapping.idTag : ''
      const useProfileIdAsIdTag = !!(profile && profile.id && idTag === profile.id)
      this.form = {
        idTag: useProfileIdAsIdTag ? profile.id : idTag,
        originalIdTag: idTag,
        originalTransportationProfileId: this.mapping && this.mapping.transportationProfileId ? this.mapping.transportationProfileId : '',
        transportationProfile: profile,
        vehicleClass: this.mapping && this.mapping.vehicleClass ? this.mapping.vehicleClass : 'fleet_bus',
        notes: this.mapping && this.mapping.assignmentNotes ? this.mapping.assignmentNotes : '',
        useProfileIdAsIdTag
      }
    },
    onDialogInput (nextValue) {
      this.$emit('input', nextValue)
    },
    close () {
      if (this.apiInProgress || this.deleteInProgress) {
        return
      }
      this.$emit('input', false)
    },
    async deleteMapping () {
      if (this.deleteInProgress || !this.mapping || !this.form.originalIdTag) {
        return
      }
      if (!confirm('Delete idTag mapping for ' + this.form.originalIdTag + '?')) {
        return
      }

      this.deleteInProgress = true
      try {
        await deleteOcppIdTagAssignment({
          idTag: this.form.originalIdTag,
          transportationProfileId: this.form.originalTransportationProfileId || undefined
        })
        addAlert({ message: 'OCPP idTag mapping deleted', type: 'success' })
        this.$emit('deleted', {
          idTag: this.form.originalIdTag,
          transportationProfileId: this.form.originalTransportationProfileId || undefined
        })
        this.$emit('input', false)
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.deleteInProgress = false
      }
    },
    async save () {
      if (!this.canSave) {
        return
      }

      const idTag = this.resolvedIdTag
      const transportationProfileId = this.selectedTransportationProfileId
      this.apiInProgress = true
      try {
        await assignOcppIdTagProfile({
          idTag,
          transportationProfileId,
          vehicleClass: this.form.vehicleClass || 'fleet_bus',
          notes: this.form.notes
        })

        if (
          this.form.originalIdTag &&
          (this.form.originalIdTag !== idTag || this.form.originalTransportationProfileId !== transportationProfileId)
        ) {
          await deleteOcppIdTagAssignment({
            idTag: this.form.originalIdTag,
            transportationProfileId: this.form.originalTransportationProfileId || undefined
          })
        }

        addAlert({ message: 'OCPP idTag mapping saved', type: 'success' })
        this.$emit('saved', {
          idTag,
          transportationProfileId
        })
        this.$emit('input', false)
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    }
  }
}
</script>
